import { Button, Colors, Popover } from '@blueprintjs/core';
import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import VendorSelect from 'Components/Generic/BOSFilterSelect/VendorSelect';
import ErrorMessage from 'Components/Generic/Misc/ErrorMessage';
import { SITE_PAGES } from 'Config';
import ChargeAllocationData from 'Data/ChargeAllocationData';
import { getOrgNameFromId } from 'Data/context/Organizations';
import { getVendorNameFromId } from 'Data/context/Vendors';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import {
  CHARGE_ALLOCATION_STATUS,
  MISMATCHED_CORE_WARNING_TEXT,
} from 'Pages/Transactions/Enum';
import { useTransactionAndChargeAllocationContext } from 'Pages/Transactions/TransactionAndChargesContext';
import TransactionLanding from 'Pages/Transactions/TransactionLanding';
import { useEffect } from 'react';

const TransactionReview = () => {
  const [persistentFilter, updateFilterField] =
    usePersistentFiltering(`TR_FILTER`);

  let title = <span>Review Imported Transactions</span>;

  const {
    getHeaders,
    getDataTable,
    resetState,
    statusClicked,
    updateOrg,
    updateVendor,
    EditableFieldWrapper,
  } = useTransactionAndChargeAllocationContext(updateFilterField);

  useEffect(() => {
    resetState();
  }, []);

  const isCaApprovable = (ca) => {
    return (
      ca.transaction &&
      ca.assigned_charge_account_id &&
      ca.status === CHARGE_ALLOCATION_STATUS.UNREVIEWED.key &&
      ca.category &&
      ca.date_posted
    );
  };

  const isSameCore = (ca) => {
    return (
      !!ca.charge_account?.parent_organization_id &&
      !!ca.transaction?.parent_organization_id &&
      ca.charge_account.parent_organization_id ===
        ca.transaction.parent_organization_id
    );
  };

  let HEADERS = getHeaders({
    persistentFilter,
    updateFilterField,
    fromState: { fromReview: true },
  });

  const headers = [
    HEADERS.REF_NUMBER,
    HEADERS.DATE_ENTERED,

    {
      ...HEADERS.CORE_FACILITY,
      render(ca) {
        return (
          <EditableFieldWrapper
            ca={ca}
            fieldName={'org'}
            fieldText={getOrgNameFromId(
              ca.transaction?.parent_organization_id,
            )}>
            <OrganizationSelect
              key={ca.transaction?.parent_organization_id}
              defaultSelectedId={ca.transaction?.parent_organization_id}
              onSelectOrganization={(org) => updateOrg(ca, org || null)}
              isRequired
            />
          </EditableFieldWrapper>
        );
      },
      getText: (chargeAllocation) => {
        return getOrgNameFromId(
          chargeAllocation.transaction?.parent_organization_id,
        );
      },
    },

    {
      ...HEADERS.VENDOR,
      render(ca) {
        return (
          <EditableFieldWrapper
            ca={ca}
            fieldName={'vendor'}
            fieldText={getVendorNameFromId(ca.transaction?.vendor_id)}>
            <VendorSelect
              key={ca.transaction?.vendor_id}
              defaultSelectedId={ca.transaction?.vendor_id}
              onSelectVendor={(vendor) => updateVendor(ca, vendor || null)}
              isRequired
            />
          </EditableFieldWrapper>
        );
      },
    },

    {
      ...HEADERS.DESCRIPTION,
      minWidth: 148,
    },
    {
      ...HEADERS.CATEGORY,
      filterable: false,
      renderHeader: null,
      width: 88,
    },
    {
      ...HEADERS.TYPE,
      filterable: false,
      renderHeader: null,
      width: 88,
    },
    {
      ...HEADERS.ACCOUNT,
      filterable: false,
      renderHeader: null,
      width: 92,
      isRequired: true,
    },

    {
      ...HEADERS.EXPENSE_CODE,
      width: 92,
      isRequired: true,
    },

    HEADERS.AMOUNT,

    {
      ...HEADERS.STATUS,
      width: 94,
      centeredHeader: true,
      filterable: false,
      renderHeader: null,
      render(ca) {
        return (
          <ConditionalWrapper
            wrapper={(children) => (
              <Popover
                content={
                  <div
                    style={{
                      padding: 8,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}>
                    {!isCaApprovable(ca) && (
                      <ErrorMessage small message={'Missing information'} />
                    )}
                    <ErrorMessage
                      small
                      message={MISMATCHED_CORE_WARNING_TEXT}
                    />
                  </div>
                }
                interactionKind={'hover'}
                hoverOpenDelay={0}
                hoverCloseDelay={0}
                fill>
                {children}
              </Popover>
            )}
            condition={!isSameCore(ca) || !isCaApprovable(ca)}>
            <Button
              minimal
              style={{
                fontSize: 12,
                width: '100%',
                height: '100%',
                textAlign: 'center',
                whiteSpace: 'normal',
                color: Colors.WHITE,
                backgroundColor: CHARGE_ALLOCATION_STATUS.UNREVIEWED.color,
                opacity: !isCaApprovable(ca) ? 0.7 : 1,
                cursor: !isCaApprovable(ca) ? 'not-allowed' : 'pointer',
              }}
              onClick={() =>
                isCaApprovable(ca) &&
                statusClicked(ca, CHARGE_ALLOCATION_STATUS.OPEN)
              }>
              {'Approve'}
            </Button>
          </ConditionalWrapper>
        );
      },
    },

    HEADERS.DELETE,
  ];

  let breadcrumb = [SITE_PAGES.TRANSACTION_LANDING, SITE_PAGES.TRANS_REVIEW];

  let pageContent = getDataTable({
    title,
    dataKey: 'TR',
    dataQueryFunction: ChargeAllocationData.getCAListForReview,
    dataExportFunction: null,
    persistentFilter,
    updateFilterField,
    headers,
  });

  return (
    <TransactionLanding breadcrumbs={breadcrumb}>
      {pageContent}
    </TransactionLanding>
  );
};

export default TransactionReview;
